import { useState, useRef } from "react";
import { applyPromo } from "@/mocks/promoCodes";

interface Course {
  id: number;
  title: string;
  price: number;
  nextStart: string;
  instructor: { name: string };
}

interface Props {
  course: Course;
  onClose: () => void;
}

type Step = 1 | 2 | 3;

interface PromoState {
  code: string;
  applied: boolean;
  valid: boolean | null;
  discount: number;
  description: string;
  finalPrice: number;
}

export default function EnrollModal({ course, onClose }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", experience: "", goal: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [promo, setPromo] = useState<PromoState>({
    code: "",
    applied: false,
    valid: null,
    discount: 0,
    description: "",
    finalPrice: course.price,
  });
  const [promoInput, setPromoInput] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const promoInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setStep(2);
  };

  const handleApplyPromo = () => {
    if (!promoInput.trim()) return;
    setPromoChecking(true);
    // Simulate a brief async check
    setTimeout(() => {
      const result = applyPromo(promoInput, course.price);
      setPromo({
        code: promoInput.toUpperCase().trim(),
        applied: result.valid,
        valid: result.valid,
        discount: result.discount,
        description: result.description,
        finalPrice: result.finalPrice,
      });
      setPromoChecking(false);
    }, 600);
  };

  const handleRemovePromo = () => {
    setPromo({
      code: "",
      applied: false,
      valid: null,
      discount: 0,
      description: "",
      finalPrice: course.price,
    });
    setPromoInput("");
    setTimeout(() => promoInputRef.current?.focus(), 50);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const body = new URLSearchParams({
        ...form,
        course: course.title,
        originalPrice: `$${course.price}`,
        promoCode: promo.applied ? promo.code : "None",
        discount: promo.applied ? `-$${promo.discount}` : "$0",
        finalPrice: `$${promo.finalPrice}`,
        startDate: course.nextStart,
      });
      await fetch("https://readdy.ai/api/form/d79u6hprcamcd36dbnog", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setStep(3);
    } catch (_e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-lg overflow-hidden max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="bg-black px-6 py-5 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-xs font-medium mb-1">Enrolling in</p>
              <h2 className="text-white font-black text-lg leading-tight">{course.title}</h2>
              <div className="flex items-center gap-3 mt-2 text-white/80 text-xs">
                <span><i className="ri-calendar-line mr-1" />Starts {course.nextStart}</span>
                <span><i className="ri-user-line mr-1" />{course.instructor.name}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white cursor-pointer ml-4 flex-shrink-0"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Step indicator */}
          {step < 3 && (
            <div className="flex items-center gap-2 mt-4">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step > s
                        ? "bg-[#C8F135] text-black"
                        : step === s
                        ? "bg-[#C8F135] text-black"
                        : "bg-white/20 text-white"
                    }`}
                  >
                    {step > s ? <i className="ri-check-line text-xs" /> : s}
                  </div>
                  <span className="text-white/70 text-xs">
                    {s === 1 ? "Your Info" : "Review & Pay"}
                  </span>
                  {s < 2 && <div className="w-8 h-px bg-white/30" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {/* ── STEP 1: Personal Info ── */}
          {step === 1 && (
            <form onSubmit={handleStep1} className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">Full Name *</label>
                  <input
                    type="text" name="name" value={form.name}
                    onChange={handleChange} required placeholder="John Smith"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C8F135] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">Email *</label>
                  <input
                    type="email" name="email" value={form.email}
                    onChange={handleChange} required placeholder="john@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C8F135] transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">Phone</label>
                  <input
                    type="tel" name="phone" value={form.phone}
                    onChange={handleChange} placeholder="+1 (555) 000-0000"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C8F135] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">Company</label>
                  <input
                    type="text" name="company" value={form.company}
                    onChange={handleChange} placeholder="Your company"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C8F135] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1.5 block">Experience Level</label>
                <select
                  name="experience" value={form.experience} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C8F135] transition-colors cursor-pointer appearance-none"
                >
                  <option value="">Select your level</option>
                  <option>Complete Beginner</option>
                  <option>Some Experience</option>
                  <option>Intermediate</option>
                  <option>Advanced Professional</option>
                </select>
              </div>
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-black text-[#C8F135] font-bold text-sm cursor-pointer hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
              >
                Continue <i className="ri-arrow-right-line" />
              </button>
            </form>
          )}

          {/* ── STEP 2: Review + Promo + Confirm ── */}
          {step === 2 && (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="academy-enrollment"
              className="flex flex-col gap-5"
            >
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-3">
                  Order Summary
                </p>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600 truncate pr-4">{course.title}</span>
                  <span className="font-bold text-gray-900 flex-shrink-0">${course.price}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mb-3">
                  <span>Starts {course.nextStart} · Live Online</span>
                </div>

                {/* Discount row - only shown when promo applied */}
                {promo.applied && (
                  <div className="flex justify-between text-sm mb-2 text-emerald-600">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-price-tag-3-line" />
                      Promo: <strong>{promo.code}</strong>
                      <span className="text-xs text-gray-400">({promo.description})</span>
                    </span>
                    <span className="font-bold flex-shrink-0">-${promo.discount}</span>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-3 flex justify-between items-baseline">
                  <span className="font-bold text-gray-900">Total</span>
                  <div className="text-right">
                    {promo.applied && (
                      <span className="text-sm text-gray-400 line-through mr-2">
                        ${course.price}
                      </span>
                    )}
                    <span className="font-black text-xl" style={{color: "#9bc400"}}>
                      ${promo.finalPrice}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Promo Code Input ── */}
              <div>
                <label className="text-xs text-gray-500 font-medium mb-2 block flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line" style={{color: "#9bc400"}} />
                  Promo / Discount Code
                </label>

                {promo.applied ? (
                  /* Applied state */
                  <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-500 flex-shrink-0">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-emerald-700">{promo.code}</div>
                      <div className="text-xs text-emerald-600">{promo.description}, saving ${promo.discount}</div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePromo}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  /* Input state */
                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-2">
                      <input
                        ref={promoInputRef}
                        type="text"
                        value={promoInput}
                        onChange={(e) => {
                          setPromoInput(e.target.value.toUpperCase());
                          if (promo.valid === false) {
                            setPromo((p) => ({ ...p, valid: null }));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleApplyPromo();
                          }
                        }}
                        placeholder="Enter code e.g. CPI20"
                        maxLength={20}
                        className={`flex-1 border rounded-xl px-4 py-2.5 text-sm font-mono font-semibold tracking-widest uppercase focus:outline-none transition-colors ${
                          promo.valid === false
                            ? "border-red-300 bg-red-50 text-red-700 focus:border-red-400"
                            : "border-gray-200 text-gray-800 focus:border-[#C8F135]"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={!promoInput.trim() || promoChecking}
                        className="px-5 py-2.5 rounded-xl bg-black text-[#C8F135] text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-zinc-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        {promoChecking ? (
                          <><i className="ri-loader-4-line animate-spin" /> Checking</>
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>

                    {/* Invalid feedback */}
                    {promo.valid === false && (
                      <div className="flex items-center gap-1.5 text-xs text-red-500">
                        <i className="ri-error-warning-line" />
                        Invalid or expired promo code. Please try again.
                      </div>
                    )}

                    {/* Hint */}
                    {promo.valid === null && (
                      <p className="text-xs text-gray-400">
                        Have a code from a campaign or referral? Enter it above for an instant discount.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Goal textarea */}
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1.5 block">
                  What&apos;s your main goal? <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  name="goal" value={form.goal} onChange={handleChange}
                  rows={2} maxLength={300}
                  placeholder="e.g. Career change, promotion, personal project..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C8F135] transition-colors resize-none"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-black text-[#C8F135] font-bold text-sm cursor-pointer hover:bg-zinc-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin" /> Processing...</>
                  ) : (
                    <><i className="ri-secure-payment-line" /> Confirm for ${promo.finalPrice}</>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center">
                Our team will contact you with payment details within 24 hours.
              </p>
            </form>
          )}

          {/* ── STEP 3: Success ── */}
          {step === 3 && (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C8F135]/20 mb-4">
                <i className="ri-check-line text-[#C8F135] text-3xl" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">You&apos;re Enrolled!</h3>
              <p className="text-gray-500 text-sm mb-1">
                Welcome to <strong>{course.title}</strong>
              </p>

              {/* Show savings if promo was used */}
              {promo.applied && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2 my-3">
                  <i className="ri-price-tag-3-line text-emerald-500 text-sm" />
                  <span className="text-emerald-700 text-sm font-semibold">
                    You saved ${promo.discount} with code {promo.code}!
                  </span>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-4 w-full text-left mb-5 mt-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Course</span>
                  <span className="font-semibold text-gray-800 text-right max-w-[60%]">{course.title}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Starts</span>
                  <span className="font-semibold text-gray-800">{course.nextStart}</span>
                </div>
                {promo.applied && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Promo Applied</span>
                    <span className="font-semibold text-emerald-600">{promo.code} (-${promo.discount})</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-gray-200 pt-2 mt-2">
                  <span className="font-bold text-gray-900">Total Paid</span>
                  <span className="font-black" style={{color: "#9bc400"}}>${promo.finalPrice}</span>
                </div>
              </div>

              <p className="text-gray-400 text-sm mb-6">
                Confirmation sent to <strong className="text-gray-700">{form.email}</strong>. Our team will reach out within 24 hours with onboarding details.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-black text-[#C8F135] font-bold text-sm cursor-pointer hover:bg-zinc-800 transition-colors"
              >
                Back to Academy
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
